import { Typography, Box, Button, Divider, useMediaQuery } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import Modal from 'components/Modal/Modal'
import theme from 'lib/theme'
import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import axios from 'lib/axiosConfig'
import { useAlert } from 'providers/AlertProvider'
import { BorderLinearProgress, NeutralBtn } from './Orders.styled'

type CancelOrdersModalProps = {
  orders: { id: string; visibleId: string }[]
  handleCloseModal: () => void
  modal: { right: boolean }
}

export const CancelOrdersModal = ({
  orders,
  handleCloseModal,
  modal,
}: CancelOrdersModalProps) => {
  const { t } = useTranslation()
  const [cancelling, setCancelling] = useState(false)
  const queryClient = useQueryClient()
  const mutation = useMutation((value: string) =>
    axios.patch(`/v1/orders/${value}/cancel`)
  )
  const [failed, setFailed] = useState<string[]>([])
  const [success, setSuccess] = useState<string[]>([])
  const abortRef = useRef(false)
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialValues = {
    selectedOrders: orders,
  }

  const sendRequestChunk = async (
    values: { id: string; visibleId: string }[],
    successRes: string[],
    failedRes: string[]
  ) => {
    const requestChunk = values.map((value) =>
      mutation.mutateAsync(value.id).catch((err) => err)
    )

    await Promise.allSettled(requestChunk)
      .then((res) => {
        res.forEach((r) => {
          if (r?.status === 'fulfilled') {
            if (r?.value?.status === 204) {
              const order = r.value.config.url.split('/')[3]
              successRes.push(order)
            } else {
              const order = r.value.config.url.split('/')[3]
              const visibleId =
                values.find((o) => o.id === order)?.visibleId || ''
              failedRes.push(visibleId)
            }
          }
        })
      })
      .catch(() =>
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      )

    setSuccess([...success, ...successRes])
    setFailed([...failed, ...failedRes])
  }

  const handleSubmit = async (values: { id: string; visibleId: string }[]) => {
    setCancelling(true)

    const failedRes: string[] = []
    const successRes: string[] = []
    const splitArr: { id: string; visibleId: string }[][] = []
    const size = 10
    const temp = [...values]
    while (temp.length > 0) {
      splitArr.push(temp.splice(0, size))
    }

    for (let i = 0; i < splitArr.length; i += 1) {
      if (abortRef.current) {
        break
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        resolve(sendRequestChunk(splitArr[i], successRes, failedRes))
      })
    }
  }
  const handleClose = () => {
    setCancelling(false)
    setFailed([])
    setSuccess([])
    queryClient.invalidateQueries(['getOrders'])
    handleCloseModal()
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => handleSubmit(values.selectedOrders)}>
      {({ submitForm }) => (
        <Modal
          showButton={false}
          setModal={handleClose}
          modal={modal}
          title={t('cancelOrders')}>
          <Form>
            {cancelling ? (
              <Box key={success.join() + failed.join()}>
                <Typography>
                  {success.length}/{orders.length} {t('ordersCancelled')}
                </Typography>
                <BorderLinearProgress
                  variant='determinate'
                  value={(success.length / orders.length) * 100}
                />
                <NeutralBtn
                  sx={{ marginTop: theme.spacing(2) }}
                  onClick={() => {
                    abortRef.current = true
                  }}>
                  {t('stop')}
                </NeutralBtn>
                <Divider sx={{ marginTop: theme.spacing(4) }} />
                {failed.map((orderNumber) => (
                  <Box
                    key={orderNumber}
                    display='flex'
                    alignItems='center'
                    m={1}>
                    <WarningIcon
                      fontSize='inherit'
                      sx={{ fill: theme.palette.error.main }}
                    />
                    <Typography variant='body2' ml={1}>
                      {t('order')} {orderNumber}
                    </Typography>
                    <Typography
                      ml={1}
                      variant='body2'
                      sx={{ color: theme.palette.text.secondary }}>
                      {t('cancellationFailed')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                <Typography>
                  {t('confirmCancelOrders', { orderNumber: orders.length })}
                </Typography>
                <Box
                  mt={4}
                  display='flex'
                  justifyContent='space-between'
                  flexDirection={isMobile ? 'column' : 'row'}>
                  <NeutralBtn onClick={handleClose}>
                    {t('exitCancelOrders')}
                  </NeutralBtn>
                  <Button
                    sx={{
                      marginTop: isMobile ? theme.spacing(1) : 0,
                      backgroundColor: theme.palette.error.main,
                      color: theme.palette.commonColors.white,
                      '&:hover': { backgroundColor: theme.palette.error.light },
                      padding: '2px 46px',
                    }}
                    onClick={() => submitForm()}>
                    {t('confirmCancelOrdersButton', {
                      orderNumber: orders.length,
                    })}
                  </Button>
                </Box>
              </>
            )}
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
